import React, {useState, useEffect, useRef, useCallback, useMemo} from 'react'
import {Modal, Button} from 'react-bootstrap'
import {Contact} from '../type'
import {useI18n} from '../../../../Utils/_helpers/useI18n'
import {KTSVG} from '../../../../../_metronic/helpers'
import {useDispatch, useSelector} from 'react-redux'
import {GetAllTeamList} from '../../../UserPages/accounts/Overview/OverviewSlice/TeamListSlice'
import {getUserData} from '../../../../modules/auth/components/Frontend_Auth_Components/UserLoginRedux/UserLoginSlice'
import {Tag, ContactData} from '../../../../../services/cellcast.v2.types'
import InputPromoAction from '../../../../Components/InputPromoAction'
import {useNavigate} from 'react-router-dom'
import {RoutePaths} from '../../../../routing/RoutePath'
import {toast} from 'react-toastify'
import {useVoiceRecorder} from '../hooks/useVoiceRecorder'
import {useMicLevel} from '../hooks/useMicLevel'
import {Spinner} from 'react-bootstrap'
import '../VoiceRecordModal.scss'
import {useGetRawContactDetailQuery} from '../../../../../services/cellcast.v2'
import InputVehicleTypeOption from '../../../../Components/InputVehicleTypeOption'

import { PortalNotificationItem, NotificationState } from '../../../../Redux/FrontEnd/NotificationSlice/NotificationSlice'
const findMostRecentNotificationIdByContactId = (
  notifications: PortalNotificationItem[],
  contactId: string,
  maxScan = 200,
): string | null => {
  const totalCount = notifications.length
  const startIndex = Math.max(0, totalCount - maxScan)

  let latestNotificationId: string | null = null
  let latestTimestamp = ''

  for (let i = startIndex; i < totalCount; i++) {
    const candidate = notifications[i]
    if (candidate?.notificationDetails?.rawContactId !== contactId) continue

    const lastModified = candidate.updatedAt ?? candidate.createdAt ?? ''
    if (!latestNotificationId || lastModified > latestTimestamp) {
      latestNotificationId = candidate._id
      latestTimestamp = lastModified
    }
  }

  return latestNotificationId
}

interface TagOption {
  value: string
  label: string
  color: string
}

interface ContactEditModalProps {
  show: boolean
  contact: Contact | null
  onClose: () => void
  onUpdate: (contact: Partial<ContactData>) => void | Promise<void>
  onVoiceSaved?: () => void
}

export const ContactEditModal: React.FC<ContactEditModalProps> = ({
  show,
  contact,
  onClose,
  onUpdate,
  onVoiceSaved,
}) => {
  const {t} = useI18n()
  const [formData, setFormData] = useState<Partial<Contact>>({})
  const [interestedVehicleTypes, setInterestedVehicleTypes] = useState<string[]>([])
  const [selectedTags, setSelectedTags] = useState<TagOption[]>([])
  const [selectedPromoActions, setSelectedPromoActions] = useState<string[]>([])
  const [openContactInfo, setOpenContactInfo] = useState(true)
  const [noteMode, setNoteMode] = useState<'voice' | 'text'>('voice')
  const [noteText, setNoteText] = useState('')
  const [isEditingName, setIsEditingName] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [hasSavedRecording, setHasSavedRecording] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const userData = useSelector(getUserData)
  const navigate = useNavigate()
  // Redux for team members (assign to)
  const dispatch = useDispatch()
  const OwnerId = userData?.user?._id
  const teamList = useSelector((state: any) => {
    const teamData = state?.teamlist?.TeamList?.items || []
    const user = state.userDetails.userDetails?.data?.user
    return [user, ...teamData]
  })

const selectPortalNotifications = (state: NotificationState) =>
  state.notification?.portal

const portal = useSelector(selectPortalNotifications)
const latestRelevantPortalNotificationId = useMemo(() => {
  if (!show) return null
  const contactId = contact?.id
  if (!contactId || !portal?.length) return null
  return findMostRecentNotificationIdByContactId(portal, contactId)
}, [show, contact?.id, portal])

  const {data: rawContactDetail, refetch: refetchRawContactDetail} = useGetRawContactDetailQuery(
    {id: contact?.id || ''},
    {
      skip: !show || !contact?.id,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
    }
  )
  const lastContactIdRef = useRef<string | null>(null)
  // Fetch team list when modal opens
  useEffect(() => {
    if (show && OwnerId) {
      dispatch(GetAllTeamList({ownerId: OwnerId}))
    }
  }, [show, OwnerId, dispatch])

  useEffect(() => {
    if (!show || !contact?.id) return

    lastContactIdRef.current = contact.id

    // Always update form data when contact changes or modal opens
    // This ensures we always use the latest data from API
    setFormData({
      fullName: contact.fullName || '',
      email: contact.email || '',
      phone: contact.phone || '',
      cardName: contact.cardName || '',
      noteVoice: contact.noteVoice || '',
      source: contact.source || '',
      status: contact.status || 'unverified',
      sales: contact.sales || false,
      assignedTo: contact.assignedTo || null,
      intent: contact.intent || '',
      sentiment: contact.sentiment || '',
      statusAiSummaryNote: contact.statusAiSummaryNote || '',
      welcomeMessage: contact.welcomeMessage || '',
      noteText: contact.noteText || '',
      lastVoice: contact.lastVoice || null,
    })
    setInterestedVehicleTypes(contact.interestedVehicleTypes || [])

    // Ensure local noteText state follows API data on contact switch
    setNoteText(contact.noteText || '')

    // Reset recording saved state when switching contacts
    setHasSavedRecording(false)

    // Convert tags to select format
    const tags = contact.tags || ([] as Tag[])
    const tagOptions = tags.map((tag) => {
      if (typeof tag === 'string') {
        return {
          value: tag,
          label: tag,
          color: '#ffffff',
        }
      }
      return {
        value: tag._id,
        label: tag.name,
        color: tag.color || '#ffffff',
      }
    })
    setSelectedTags(tagOptions)

    // Set promo actions
    setSelectedPromoActions(contact.promoSettings?.map((promo) => promo.id) || [])
  }, [show, contact])

  // Prefer latest detail from API if it includes vehicle types
  useEffect(() => {
    if (!show || !contact?.id) return
    const detailUnknown = rawContactDetail?.data as unknown
    if (!detailUnknown) return
    const detail = detailUnknown as {id?: string; interestedVehicleTypes?: unknown}
    if (detail?.id && detail.id !== contact.id) return
    if (Array.isArray(detail.interestedVehicleTypes)) {
      const ids = detail.interestedVehicleTypes.filter((v): v is string => typeof v === 'string')
      setInterestedVehicleTypes(ids)
    }
  }, [show, contact?.id, rawContactDetail?.data])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isSaving) return
    if (contact) {
      const updatedContactData = {
        id: contact.id,
        fullName: formData.fullName || '',
        email: formData.email || null,
        phone: formData.phone || '',
        noteVoice: formData.noteVoice || null,
        source: formData.source || '',
        cardName: formData.cardName || '',
        status: formData.status,
        intent: formData.intent,
        user: contact.id,
        tags: selectedTags.map((tag) => tag.value),
        promoSettings: selectedPromoActions,
        sales: formData.sales || false,
        assignedTo: formData.assignedTo ? formData.assignedTo.id : null,
        noteText: noteText || '',
        welcomeMessage: formData.welcomeMessage || '',
        value: formData.value,
        leadStatus: formData.leadStatus || '',
        interestedVehicleTypes,
        interestedVehicleType: interestedVehicleTypes[0] || null,
      }
      try {
        setIsSaving(true)
        await onUpdate(updatedContactData)
      } finally {
        setIsSaving(false)
      }
    }
  }

  // Ensure cleanup when modal is programmatically hidden by parent
  // We'll attach the effect AFTER the hook is declared (see below) to avoid temporal dead zone.

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const {name, type, value} = e.target
    if (name === 'assignedTo') {
      const member = teamList.find((m: any) => m._id === value)
      setFormData((prev) => ({
        ...prev,
        assignedTo: member
          ? {id: member._id, name: member.firstName + ' ' + member.lastName}
          : null,
      }))
    } else if (name === 'intent') {
      setFormData((prev) => ({
        ...prev,
        intent: value,
      }))
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
      }))
    }
  }

  const handleCall = () => {
    const raw = formData.phone || ''
    const sanitized = String(raw).replace(/\D/g, '')
    if (!sanitized) {
      toast.error('No phone number found')
      return
    }
    // Open dialer first to avoid popup blocking
    window.open(`tel:${sanitized}`, '_self')
    // Update callLastTime on rawContact (datetime in ISO string)
    try {
      if (contact?.id) {
        const nowIso = new Date().toISOString()
        // fire-and-forget; parent handler may close modal
        void onUpdate({id: contact.id, callLastTime: nowIso} as any)
      }
    } catch (_) {}
  }

  const handleGoToInbox = (phone?: string) => {
    const raw = phone || formData.phone || ''
    const sanitized = String(raw).replace(/\D/g, '')
    if (!sanitized) {
      toast.error('No phone number found')
      return
    }
    navigate({pathname: RoutePaths.inbox, search: `?receiver=${sanitized}`})
  }

  // Voice recorder hook
  const {
    isRecording,
    isPaused,
    canStop,
    isUploading,
    startRecording,
    stopRecording,
    saveRecording,
    discardRecording,
    forceStopRecording,
    hasRecordedAudio,
    recordedAudioUrl,
    recordList,
    refetch,
    resetRecordingState,
  } = useVoiceRecorder({
    rawContactId: contact?.id || '',
    autoUploadOnStop: false,
    onSuccess: (result: any) => {
      try {
        const status = result?.data?.data?.status || result?.data?.status || result?.status
        if (status) {
          setFormData((prev) => ({...prev, statusAiSummaryNote: String(status)}))
          setHasSavedRecording(true)
        }
      } catch (_) {}
    },
  })

  const micLevel = useMicLevel(isRecording)

  // Timer effect
  useEffect(() => {
    if (isRecording && !isPaused) {
      intervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1)
      }, 1000)
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [isRecording, isPaused])

  // Reset timer when recording stops or modal closes
  useEffect(() => {
    if (!isRecording) {
      setRecordingTime(0)
    }
  }, [isRecording])

  // Sync welcomeMessage from latest raw contact detail when it changes
  useEffect(() => {
    const dataUnknown = rawContactDetail?.data as unknown
    if (!dataUnknown || !contact?.id) return

    const detail = dataUnknown as {id?: string; welcomeMessage?: unknown; statusAiSummaryNote?: unknown}
    if (detail?.id && detail.id !== contact.id) return

    setFormData((prev) => ({
      ...prev,
      welcomeMessage:
        typeof detail.welcomeMessage === 'string' ? detail.welcomeMessage : prev.welcomeMessage,
      statusAiSummaryNote:
        typeof detail.statusAiSummaryNote === 'string'
          ? detail.statusAiSummaryNote
          : prev.statusAiSummaryNote,
    }))
  }, [rawContactDetail, contact?.id])

  // Keep UI responsive while recording: debounce + only handle newest relevant notification once
  const refreshTimerRef = useRef<number | null>(null)
  const lastHandledPortalNotificationIdRef = useRef<string | null>(null)
  const pendingRefreshRef = useRef(false)

  const refreshLatestAiOutputs = useCallback(() => {
    try {
      refetchRawContactDetail()
    } catch (_) {}
    refetch()
  }, [refetchRawContactDetail, refetch])

  useEffect(() => {
    const nextId = latestRelevantPortalNotificationId
    if (!nextId) return

    if (lastHandledPortalNotificationIdRef.current === nextId) {
      return
    }
    lastHandledPortalNotificationIdRef.current = nextId

    // If recording/uploading, postpone refresh until idle to avoid UI jank.
    if (isRecording || isUploading) {
      pendingRefreshRef.current = true
      return
    }

    // Debounce multiple portal updates into a single refresh.
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current)
    }
    refreshTimerRef.current = window.setTimeout(() => {
      refreshTimerRef.current = null
      refreshLatestAiOutputs()
    }, 600)
  }, [latestRelevantPortalNotificationId, isRecording, isUploading, refreshLatestAiOutputs])

  // Run any postponed refresh right after recording/upload completes.
  useEffect(() => {
    if (isRecording || isUploading) return
    if (!pendingRefreshRef.current) return
    pendingRefreshRef.current = false
    refreshLatestAiOutputs()
  }, [isRecording, isUploading, refreshLatestAiOutputs])

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current)
        refreshTimerRef.current = null
      }
    }
  }, [])

  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  // Now that hook vars exist, watch 'show' to cleanup when hidden
  useEffect(() => {
    if (!show) {
      try {
        forceStopRecording()
      } catch (_) {}
      discardRecording()
      resetRecordingState()
      setHasSavedRecording(false)
      setRecordingTime(0)
    }
  }, [show, forceStopRecording, discardRecording, resetRecordingState])

  return (
    <Modal
      backdrop='static'
      dialogClassName='modal-dialog-centered'
      show={show}
      className='raw-contact-edit-modal'
      onHide={() => {
        try {
          forceStopRecording()
        } catch (_) {}
        discardRecording()
        resetRecordingState()
        onClose()
      }}
      size='lg'
    >
      <div className='modal-header'>
        <h1 className='p-0 mb-1'>{t('RAW_CONTACT_COLLECTION.EDIT_CONTACT')}</h1>
        <div
          className='btn btn-icon btn-sm'
          onClick={() => {
            try {
              forceStopRecording()
            } catch (_) {}
            discardRecording()
            resetRecordingState()
            onClose()
          }}
        >
          <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
        </div>
      </div>
      <div className='modal-body'>
        <div className='mb-4 d-flex align-items-center justify-content-between'>
          <div className='d-flex align-items-center gap-2'>
            {isEditingName ? (
              <input
                type='text'
                name='fullName'
                value={formData.fullName || ''}
                onChange={handleChange}
                className='form-control bg-transparent p-0 fw-bold border-0 fs-2 modal-avatar-container'
                placeholder='Full name'
                autoFocus
                onBlur={() => setIsEditingName(false)}
              />
            ) : (
              <h2 className='p-0 mb-1 d-inline-block text-truncate fw-bold fs-2 modal-title-truncate'>
                {formData.fullName || ''}
              </h2>
            )}
            {!isEditingName && (
              <button
                type='button'
                className='btn btn-sm btn-icon btn-light'
                title='Edit name'
                onClick={() => setIsEditingName(true)}
              >
                <i className='bi bi-pencil-square'></i>
              </button>
            )}
          </div>
          <div className='d-flex align-items-center gap-3 justify-content-end modal-form-container'>
            <div className='select-caret-wrapper modal-select-wrapper'>
              <select
                onChange={handleChange}
                name='intent'
                id='intent'
                value={formData.intent || ''}
                className='form-select w-100'
              >
                <option value=''>Select Intent</option>
                <option value='Hot'>Hot</option>
                <option value='Cold'>Cold</option>
              </select>
            </div>
            {/* SENTIMENT */}
            {formData.sentiment && (
              <div className='select-caret-wrapper modal-select-wrapper'>
                <div className='form-control w-100'>{formData.sentiment?.toUpperCase() || '-'}</div>
              </div>
            )}
          </div>
        </div>

        <div className='rounded-1 mb-6'>
          <div
            className='d-flex align-items-center justify-content-between cursor-pointer'
            onClick={() => setOpenContactInfo((v) => !v)}
          >
            <h2 className='p-0 mb-1'>Contact Information</h2>
            <i className={`bi ${openContactInfo ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
          </div>


          {openContactInfo && (
            <div className='px-3'>
              {/* PHONE */}
              <div className='d-flex align-items-center justify-content-between py-3 border-bottom'>
                <label className='mb-0 fw-semibold text-label'>Phone</label>
                <input
                  type='text'
                  name='phone'
                  value={formData.phone || ''}
                  onChange={handleChange}
                  className='form-control bg-transparent text-end p-0 modal-input-60'
                  placeholder='000 000 0000'
                />
              </div>
              {/* EMAIL */}
              <div className='d-flex align-items-center justify-content-between py-3 border-bottom'>
                <label className='mb-0 fw-semibold text-label'>Email</label>
                <input
                  type='email'
                  name='email'
                  value={formData.email || ''}
                  onChange={handleChange}
                  className='form-control bg-transparent text-end p-0 modal-input-60'
                  placeholder='you@example.com'
                />
              </div>
              {/* SOURCE */}
              <div className='d-flex align-items-center justify-content-between py-3 border-bottom'>
                <label className='mb-0 fw-semibold text-label'>Source</label>
                <input
                  type='text'
                  name='source'
                  value={formData.source || ''}
                  onChange={handleChange}
                  disabled
                  className='form-control bg-transparent text-end p-0 modal-input-60'
                  placeholder='Brand'
                />
              </div>
              {/* CARD NAME */}
              <div className='d-flex align-items-center justify-content-between py-3 border-bottom'>
                <label className='mb-0 fw-semibold text-label'>Cardname</label>
                <input
                  type='text'
                  name='cardName'
                  value={formData.cardName || ''}
                  onChange={handleChange}
                  disabled
                  className='form-control bg-transparent text-end p-0 modal-input-60'
                  placeholder='card-name'
                />
              </div>
              {/* LEAD OWNER (ASSIGNED TO) */}
              <div className='d-flex align-items-center justify-content-between py-3'>
                <label className='mb-0 fw-semibold text-label'>Lead Owner</label>
                <div className='d-flex align-items-center select-caret-wrapper modal-input-60-full'>
                  <select
                    name='assignedTo'
                    value={
                      typeof formData.assignedTo?.id === 'string'
                        ? (formData.assignedTo?.id as string)
                        : ''
                    }
                    onChange={handleChange}
                    className='border-0 text-end no-border-input modal-textarea-full'
                  >
                    <option value=''>Select member</option>
                    {teamList?.map((member: any) => (
                      <option key={member._id} value={member._id}>
                        {member.firstName} {member.lastName}
                      </option>
                    ))}
                  </select>
                  <i className='bi bi-caret-down-fill select-caret'></i>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className='row mb-4 px-3 pt-6 border-top'>
          <h2 className='p-0 m-0'>Customer Insights</h2>
        </div>
        <div className='row mb-6 px-6'>
          <label className='p-0 mb-3 text-label fw-bold fs-6'>Card Offers Selected</label>
          <InputPromoAction
            value={selectedPromoActions}
            onChange={setSelectedPromoActions}
            className='react-tags-selectbox rounded-1'
            name='promoSettings'
          />
        </div>
               <div className='row mb-6 px-6'>
            <label className='p-0 mb-3 text-label fw-bold fs-6'>Interested Vehicle Types</label>
            <InputVehicleTypeOption
              value={interestedVehicleTypes}
              onChange={setInterestedVehicleTypes}
              className='react-tags-selectbox rounded-1'
              name='interestedVehicleTypes'
              allowCreate={false}
            />
          </div>
        <div className='row mb-6 px-6'>
          <label className='p-0 mb-3 text-label fw-bold fs-6'>Add New Notes</label>
          <div className='d-flex align-items-center gap-3 mb-3 p-0 w-100'>
            <button
              type='button'
              className={`btn modal-button-150 ${
                noteMode === 'voice' ? 'btn-primary' : 'btn-light-primary'
              }`}
              onClick={() => setNoteMode('voice')}
            >
              Voice AI
            </button>
            <button
              type='button'
              className={`btn modal-button-150 ${
                noteMode === 'text' ? 'btn-primary' : 'btn-light-primary'
              }`}
              onClick={() => setNoteMode('text')}
            >
              Text
            </button>
          </div>
          {noteMode === 'voice' ? (
            <div className='d-flex flex-column p-0'>
              <div className='border rounded p-3'>
                <div className='d-flex align-items-center gap-3 position-relative'>
                  {(() => {
                    const micDisabled = isRecording && !isPaused && !canStop
                    const titleText = !isRecording
                      ? 'Record'
                      : canStop
                      ? 'Stop'
                      : 'After 5s you can stop'
                    return (
                      <div
                        className={`vrm-mic-wrap ${(() => {
                          if (isPaused) return 'paused'
                          if (isRecording && micLevel !== 'silent') return 'active'
                          if (isRecording) return 'recording'
                          return ''
                        })()} ${micDisabled ? 'disabled' : ''}`}
                        role='button'
                        tabIndex={micDisabled ? -1 : 0}
                        aria-disabled={micDisabled}
                        title={titleText}
                        onClick={() => {
                          if (isUploading || micDisabled) return
                          if (!isRecording) {
                            startRecording()
                            toast.info('Recording started')
                          } else if (canStop) {
                            stopRecording()
                            toast.info('Recording stopped')
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            if (isUploading || micDisabled) return
                            if (!isRecording) {
                              startRecording()
                              toast.info('Recording started')
                            } else if (canStop) {
                              stopRecording()
                              toast.info('Recording stopped')
                            }
                          }
                        }}
                      >
                        <i className={`bi ${!isRecording ? 'bi-mic-fill' : 'bi-stop-fill'}`}></i>
                      </div>
                    )
                  })()}
                  <div>
                    <div className='fw-bold fs-6'>Voice Recorder</div>
                    <div className='text-muted small'>Record a short note for this contact</div>
                  </div>

                  {/* Recording Timer - Top Right Corner */}
                  {isRecording && (
                    <div
                      className='recording-timer position-absolute'
                      style={{top: '0', right: '0'}}
                    >
                      <span className='badge bg-light text-dark fs-6 px-2 py-1 border'>
                        {formatTime(recordingTime)}
                      </span>
                    </div>
                  )}

                  {isUploading && <Spinner animation='border' size='sm' />}
                </div>

                {hasRecordedAudio && recordedAudioUrl && (
                  <div className='d-flex align-items-center justify-content-between gap-3 mt-3 flex-wrap'>
                    <audio controls src={recordedAudioUrl} className='w-100' />
                    <div className='d-flex gap-2 ms-auto'>
                      <button
                        type='button'
                        className='btn btn-sm btn-light-danger'
                        onClick={() => discardRecording()}
                        disabled={isUploading || isRecording}
                      >
                        <i className='bi bi-trash me-2'></i>
                        Delete
                      </button>
                      <button
                        type='button'
                        className='btn btn-sm btn-primary'
                        onClick={async () => {
                          if (isRecording) {
                            if (!canStop) return
                            stopRecording()
                          }
                          try {
                            await Promise.resolve(saveRecording())
                            toast.success(
                              'Recording submitted successfully! It will take about a minute to an hour to process and transcribe your audio.',
                              {
                                autoClose: 5000,
                                hideProgressBar: false,
                              }
                            )
                            try {
                              onVoiceSaved && onVoiceSaved()
                            } catch (_) {}
                          } catch (_) {}
                        }}
                        disabled={isRecording || isUploading || !hasRecordedAudio}
                      >
                        {isUploading ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Status display - only show when recording has been saved */}
                {hasSavedRecording && (
                  <div className='mt-2'>
                    <div>
                      <span className='fw-bold me-2'>Status:</span>
                      <span>
                        {(formData.statusAiSummaryNote || '').toLowerCase() === 'pending'
                          ? 'Transcribing.'
                          : formData.statusAiSummaryNote || ''}
                      </span>
                    </div>
                    {(formData.statusAiSummaryNote || '').toLowerCase() === 'pending' && (
                      <div className='text-muted small mt-1'>
                        You can close this modal and come back later to see the transcript.
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className='mt-6'>
                <label className='fw-bold fs-6 mb-2 text-label'>Transcript</label>
                <div
                  className={`bg-light border rounded p-3 ${
                    (formData.statusAiSummaryNote || '').toLowerCase() === 'pending'
                      ? 'loading-border'
                      : ''
                  }`}
                  style={{minHeight: '80px', resize: 'vertical', backgroundColor: '#f8f9fa'}}
                >
                  {(formData.statusAiSummaryNote || '').toLowerCase() === 'pending' ? (
                    <div className='d-flex align-items-center gap-2 text-muted'>
                      <Spinner animation='border' size='sm' />
                      <span>Transcribing...</span>
                    </div>
                  ) : (
                    <div
                      className='text-dark'
                      style={{whiteSpace: 'pre-wrap', fontSize: '14px', lineHeight: '1.4'}}
                    >
                      {recordList?.data?.items?.[0]?.transcript || ''}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className='d-flex flex-column gap-3 p-0'>
              <textarea
                className='form-control'
                rows={4}
                placeholder='Type your note here'
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
              />
            </div>
          )}
        </div>
        <div className='row mb-6 px-6'>
          <label className='p-0 mb-3 text-label fw-bold fs-6'>Welcome Message</label>
          <div
            className={`bg-light border rounded p-3 ${
              (formData.statusAiSummaryNote || '').toLowerCase() === 'pending'
                ? 'loading-border'
                : ''
            }`}
            style={{minHeight: '100px', resize: 'vertical', backgroundColor: '#f8f9fa'}}
          >
            {(formData.statusAiSummaryNote || '').toLowerCase() === 'pending' ? (
              <div className='d-flex align-items-center gap-2 text-muted'>
                <Spinner animation='border' size='sm' />
                <span>Generating welcome message...</span>
              </div>
            ) : (
              <textarea
                className='form-control bg-transparent border-0 p-0'
                rows={4}
                placeholder='Type your welcome message here'
                value={formData.welcomeMessage || ''}
                onChange={(e) => setFormData((prev) => ({...prev, welcomeMessage: e.target.value}))}
                style={{
                  resize: 'none',
                  outline: 'none',
                  boxShadow: 'none',
                  fontSize: '14px',
                  lineHeight: '1.4',
                  color: '#333',
                }}
              />
            )}
          </div>
          {/* OPENERS */}
          {formData.lastVoice?.highlights && formData.lastVoice?.highlights?.length > 0 && (
            <div className='row mb-4'>
              <label className='p-0 mb-3 text-label fw-bold fs-6'>Openers</label>
              <ul className='ps-6 m-0'>
                {formData.lastVoice?.highlights?.map((opener, idx) => (
                  <li key={idx} className='mb-2'>
                    {opener}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className='row mb-6 px-6'>
          <div className='d-flex gap-3 p-0'>
            <button
              type='button'
              className='btn btn-light-primary d-flex align-items-center gap-2'
              onClick={handleCall}
            >
              <i className='bi bi-telephone-fill'></i>
              Call
            </button>
            <button
              type='button'
              className='btn btn-primary d-flex align-items-center gap-2'
              onClick={() => handleGoToInbox(formData.phone || '')}
            >
              <i className='bi bi-chat-dots-fill'></i>
              Message
            </button>
          </div>
        </div>
        <div className='row px-3'>
          <span className='text-label fw-semibold text-gray-700 mb-1'>Deal Status</span>
          <label
            className={`d-inline-flex align-items-center gap-2 border rounded p-2 cursor-pointer transition-all ${
              formData.sales ? 'bg-light border-primary' : 'bg-white border-secondary'
            }`}
            style={{
              maxWidth: '80px',
              justifyContent: 'center',
              marginLeft: '10px',
              marginTop: '3px',
            }}
          >
            <input
              type='checkbox'
              className='form-check-input'
              checked={!!formData.sales}
              onChange={(e) => setFormData((prev) => ({...prev, sales: e.target.checked}))}
              style={{
                width: '18px',
                height: '18px',
                margin: '0',
                cursor: 'pointer',
                accentColor: formData.sales ? '#7950F2' : '#6c757d',
                border: formData.sales ? '2px solid #7950F2' : '2px solid #dee2e6',
                borderRadius: '4px',
                backgroundColor: formData.sales ? '#7950F2' : 'white',
                boxShadow: formData.sales ? '0 0 0 2px rgba(121, 80, 242, 0.25)' : 'none',
                transition: 'all 0.2s ease-in-out',
              }}
            />
            <span className={`fw-bold mb-0 ${formData.sales ? 'text-primary' : 'text-gray-600'}`}>
              SOLD
            </span>
          </label>
        </div>
      </div>

      <div className='modal-footer border-top'>
        <div className='d-flex gap-3 justify-content-end align-items-center w-100'>
          <Button
            className='btn btn-danger px-6'
            onClick={() => {
              try {
                forceStopRecording()
              } catch (_) {}
              discardRecording()
              resetRecordingState()
              onClose()
            }}
            disabled={isSaving}
          >
            {t('COMMON.CLOSE')}
          </Button>
          <Button
            className='btn-primary px-10'
            onClick={handleSubmit}
            disabled={isSaving || isRecording}
          >
            {isSaving ? 'Saving...' : t('COMMON.SAVE')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
